import { Injectable } from "@angular/core";
import { Actions, concatLatestFrom, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { concat, EMPTY } from "rxjs";
import { catchError, mergeMap, switchMap } from "rxjs/operators";
import { RoomStreamActions } from "../actions";
import {
  RoomStageLayoutFocusDeleteRequestDTO,
  RoomStageLayoutMainDeleteRequestDTO,
  RoomStreamActiveDeleteRequestDTO,
  RoomStreamActiveSetRequestDTO
} from "../dto";
import { RoomStageLayoutSelectors } from "../selectors";
import { RoomStageLayoutService, RoomStreamService } from "../services";

@Injectable()
export class RoomStreamEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly roomStageLayoutService: RoomStageLayoutService,
    private readonly roomStreamService: RoomStreamService,
    private readonly store: Store
  ) {}

  roomStreamActiveDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomStreamActions.roomStreamActiveDelete),
      concatLatestFrom(() => [
        this.store.select(RoomStageLayoutSelectors.focus),
        this.store.select(RoomStageLayoutSelectors.main)
      ]),
      switchMap(([{ roomStreamActiveDeleteCmd }, focus, main]) => {
        const {
          cmd: { roomId, streamId }
        } = roomStreamActiveDeleteCmd;
        return concat(
          streamId === main
            ? this.roomStageLayoutService.roomStageLayoutMainDelete(
                new RoomStageLayoutMainDeleteRequestDTO({
                  roomId
                })
              )
            : EMPTY,
          streamId === focus
            ? this.roomStageLayoutService.roomStageLayoutFocusDelete(
                new RoomStageLayoutFocusDeleteRequestDTO({
                  roomId
                })
              )
            : EMPTY,
          this.roomStreamService.roomStreamActiveDelete(
            new RoomStreamActiveDeleteRequestDTO({
              roomId,
              streamId
            })
          )
        ).pipe(
          mergeMap(() => [
            RoomStreamActions.roomStreamActiveDeleteSuccess({
              roomStreamActiveDeleteCmd
            })
          ]),
          catchError((_: Error) => [
            RoomStreamActions.roomStreamActiveDeleteFailure({
              error: _.message
            })
          ])
        );
      })
    )
  );

  roomStreamActiveSet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomStreamActions.roomStreamActiveSet),
      switchMap(({ roomStreamActiveSetCmd }) =>
        this.roomStreamService
          .roomStreamActiveSet(
            new RoomStreamActiveSetRequestDTO({
              roomId: roomStreamActiveSetCmd.cmd.roomId,
              streamId: roomStreamActiveSetCmd.cmd.streamId
            })
          )
          .pipe(
            mergeMap(() => [
              RoomStreamActions.roomStreamActiveSetSuccess({
                roomStreamActiveSetCmd
              })
            ]),
            catchError((_: Error) => [
              RoomStreamActions.roomStreamActiveSetFailure({
                error: _.message
              })
            ])
          )
      )
    )
  );
}
